(() => {
  if (customElements.get('hone-product-360')) return;
  const rad = Math.PI / 180;
  const multiply = (a,b) => [
    a[3]*b[0]+a[0]*b[3]+a[1]*b[2]-a[2]*b[1],
    a[3]*b[1]-a[0]*b[2]+a[1]*b[3]+a[2]*b[0],
    a[3]*b[2]+a[0]*b[1]-a[1]*b[0]+a[2]*b[3],
    a[3]*b[3]-a[0]*b[0]-a[1]*b[1]-a[2]*b[2]
  ];
  const axis = (v,a) => [...v.map(n => n*Math.sin(a/2)),Math.cos(a/2)];
  const normalize = q => { const l=Math.hypot(...q); return q.map(n=>n/l); };
  const orientation = q => {
    const [x,y,z,w]=q, m23=2*(y*z-x*w), pitch=Math.asin(-Math.max(-1,Math.min(1,m23)));
    const yaw=Math.abs(m23)<.9999999 ? Math.atan2(2*(x*z+y*w),1-2*(x*x+y*y)) : Math.atan2(-2*(x*z-y*w),1-2*(y*y+z*z));
    const roll=Math.abs(m23)<.9999999 ? Math.atan2(2*(x*y+z*w),1-2*(x*x+z*z)) : 0;
    return roll+'rad '+pitch+'rad '+yaw+'rad';
  };
  const rotations = {'big-20':[0,0],'big-15':[0,-180],'small-20':[180,90],'small-15':[180,-90],assembled:[0,0],detached:[0,0]};
  let modulePromise;
  function loadModule(url) {
    if (customElements.get('model-viewer')) return Promise.resolve();
    if (!modulePromise) modulePromise=new Promise((resolve,reject)=>{
      const script=document.createElement('script');script.type='module';script.src=url;
      script.onload=()=>customElements.whenDefined('model-viewer').then(resolve);
      script.onerror=()=>{script.remove();modulePromise=null;reject(new Error('Viewer unavailable'));};
      document.head.append(script);
    });
    return modulePromise;
  }
  class HoneProduct360 extends HTMLElement {
    connectedCallback() {
      if(this.ready)return;this.ready=true;
      this.kind=this.dataset.kind;this.options=JSON.parse(this.querySelector('[data-config]').textContent);
      this.key=this.kind==='holder'?'big-20':'detached';this.points=new Map();this.ticket=0;
      this.poster=this.querySelector('[data-poster]');this.stage=this.querySelector('[data-stage]');
      this.gesture=this.querySelector('[data-gesture]');this.explore=this.querySelector('[data-explore]');
      this.status=this.querySelector('[data-status]');this.tools=this.querySelector('[data-tools]');
      this.addEventListener('click',event=>{
        const button=event.target.closest('button');if(!button)return;
        if(button.dataset.preset){this.key=button.dataset.preset;this.select();return;}
        if(button.hasAttribute('data-explore'))this.open();
        if(button.hasAttribute('data-close'))this.close();
        if(button.hasAttribute('data-reset'))this.reset();
        if(button.dataset.zoom)this.zoom(Number(button.dataset.zoom));
      });
      this.gesture.addEventListener('pointerdown',e=>{
        e.preventDefault();this.gesture.focus({preventScroll:true});this.gesture.setPointerCapture(e.pointerId);
        this.points.set(e.pointerId,[e.clientX,e.clientY]);this.distance=this.pinchDistance();
      });
      this.gesture.addEventListener('pointermove',e=>{
        if(!this.points.has(e.pointerId)||!this.model?.loaded)return;
        e.preventDefault();const old=this.points.get(e.pointerId);
        this.points.set(e.pointerId,[e.clientX,e.clientY]);
        if(this.points.size===2){const next=this.pinchDistance();if(this.distance&&next)this.zoom(this.distance/next);this.distance=next;}
        else if(this.points.size===1)this.turn((e.clientX-old[0])*.009,(e.clientY-old[1])*.009);
      });
      const release=e=>{this.points.delete(e.pointerId);this.distance=this.pinchDistance();};
      this.gesture.addEventListener('pointerup',release);this.gesture.addEventListener('pointercancel',release);
      this.gesture.addEventListener('lostpointercapture',release);
      this.gesture.addEventListener('wheel',e=>{e.preventDefault();this.zoom(Math.exp(Math.max(-150,Math.min(150,e.deltaY))*.003));},{passive:false});
      this.gesture.addEventListener('keydown',e=>{
        const moves={ArrowLeft:[-.13,0],ArrowRight:[.13,0],ArrowUp:[0,-.13],ArrowDown:[0,.13]};
        if(moves[e.key]){e.preventDefault();this.turn(...moves[e.key]);}
        if(e.key==='+'||e.key==='='){e.preventDefault();this.zoom(.85);}
        if(e.key==='-'){e.preventDefault();this.zoom(1.15);}
        if(e.key==='Home'){e.preventDefault();this.reset();}
        if(e.key==='Escape'){e.preventDefault();this.close();this.explore.focus();}
      });
      this.select();
    }
    disconnectedCallback(){this.close();cancelAnimationFrame(this.frame);}
    pinchDistance(){const p=[...this.points.values()];return p.length===2?Math.hypot(p[1][0]-p[0][0],p[1][1]-p[0][1]):0;}
    get spec(){return this.options.positions[this.key];}
    select(){
      this.querySelectorAll('[data-preset]').forEach(b=>b.setAttribute('aria-pressed',String(b.dataset.preset===this.key)));
      this.poster.src=this.spec.poster;this.poster.alt=this.spec.label;
      this.querySelector('[data-view-label]').textContent=this.spec.label;
      if(!this.model)return;
      if(this.model.src!==this.spec.src){this.close();this.open();}
      else this.reset();
    }
    async open(){
      const ticket=++this.ticket;
      document.querySelectorAll('hone-product-360').forEach(other=>{if(other!==this)other.close();});
      this.explore.disabled=true;this.status.textContent=this.options.loading;
      try{
        await loadModule(this.dataset.viewer);if(ticket!==this.ticket)return;
        await this.poster.decode().catch(()=>{});if(ticket!==this.ticket)return;
        const model=document.createElement('model-viewer');this.model=model;model.style.opacity='0';
        this.matchPosterFrame(model);
        model.setAttribute('min-camera-orbit','auto auto 25%');model.setAttribute('max-camera-orbit','auto auto 300%');model.setAttribute('field-of-view','30deg');
        model.setAttribute('alt',this.options.modelAlt);
        model.setAttribute('environment-image','neutral');model.setAttribute('exposure','1');
        model.setAttribute('shadow-intensity','.75');model.setAttribute('shadow-softness','1');
        model.setAttribute('interaction-prompt','none');model.setAttribute('loading','eager');
        model.setAttribute('camera-orbit',this.orbit);
        model.setAttribute('aria-hidden','true');
        this.setQuaternion();model.orientation=orientation(this.q);model.cameraTarget=this.target();
        model.addEventListener('load',async()=>{
          if(this.model!==model)return;this.tools.hidden=false;this.explore.hidden=true;this.status.textContent=this.options.instructions;
          await new Promise(requestAnimationFrame);if(this.model!==model)return;
          await this.reset();if(this.model!==model)return;
          this.poster.hidden=true;model.style.opacity='1';this.explore.disabled=false;this.gesture.hidden=false;
        });
        model.addEventListener('error',()=>{if(this.model===model){this.close();this.status.textContent=this.options.error;}});
        this.stage.prepend(model);model.src=this.spec.src;
      }catch(error){if(ticket===this.ticket){this.explore.disabled=false;this.status.textContent=this.options.error;}}
    }
    close(){
      ++this.ticket;this.points?.clear();cancelAnimationFrame(this.frame);this.frame=0;this.baseRadius=0;
      this.posterResize?.disconnect();this.posterResize=null;
      if(this.model){this.model.remove();this.model=null;}
      if(!this.poster)return;this.poster.hidden=false;this.explore.hidden=false;this.explore.disabled=false;
      this.tools.hidden=true;this.gesture.hidden=true;this.status.textContent='';
    }
    matchPosterFrame(model){
      // Match the contained, CSS-scaled source image, not the smaller controls-open stage.
      const box=this.poster.getBoundingClientRect(),stageWidth=this.stage.clientWidth;
      const aspect=(this.poster.naturalWidth||1098)/(this.poster.naturalHeight||608);
      const width=Math.min(box.width,box.height*aspect),relativeWidth=width/Math.max(1,stageWidth);
      const resize=()=>{
        if(this.model!==model)return;
        const w=this.stage.clientWidth*relativeWidth;
        Object.assign(model.style,{inset:'auto',left:'50%',top:'50%',width:w+'px',height:(w/aspect)+'px',transform:'translate(-50%,-50%)'});
      };
      resize();this.posterResize=new ResizeObserver(resize);this.posterResize.observe(this.stage);
    }
    get orbit(){
      // The detached poster was exported at this absolute camera distance.
      return this.kind==='holder'?'125deg 70deg 105%':this.key==='detached'?'42deg 68deg 0.25m':'42deg 68deg 110%';
    }
    setQuaternion(){const [roll,yaw]=rotations[this.key];this.q=normalize(multiply(axis([0,1,0],yaw*rad),axis([0,0,1],roll*rad)));}
    target(){
      const [x,y,z,w]=this.q,c=this.kind==='holder'?.016625:.030450000762939453;
      return [2*(x*y-z*w)*c,(1-2*(x*x+z*z))*c,2*(y*z+x*w)*c].map(n=>n+'m').join(' ');
    }
    async reset(){
      const model=this.model;if(!model?.loaded)return;const key=this.key;this.setQuaternion();
      model.orientation=orientation(this.q);model.cameraTarget=this.target();
      await model.updateComplete;if(this.model!==model||this.key!==key)return;
      await model.updateFraming();if(this.model!==model||this.key!==key)return;
      model.fieldOfView='30deg';model.cameraOrbit=this.orbit;
      await model.updateComplete;if(this.model!==model||this.key!==key)return;
      model.jumpCameraToGoal();await model.updateComplete;
      this.baseRadius=model.getCameraOrbit().radius;
    }
    turn(dx,dy){
      if(!this.model?.loaded)return;
      const theta=(this.kind==='holder'?125:42)*rad;
      this.q=normalize(multiply(axis([Math.cos(theta),0,-Math.sin(theta)],dy),multiply(axis([0,1,0],dx),this.q)));
      if(!this.frame)this.frame=requestAnimationFrame(()=>{
        this.frame=0;if(!this.model)return;
        this.model.orientation=orientation(this.q);this.model.cameraTarget=this.target();
      });
    }
    zoom(factor){
      if(!this.model?.loaded||!this.baseRadius)return;
      const orbit=this.model.getCameraOrbit(),radius=Math.max(this.baseRadius*.5,Math.min(this.baseRadius*2.4,orbit.radius*factor));
      this.model.cameraOrbit=orbit.theta+'rad '+orbit.phi+'rad '+radius+'m';
    }
  }
  customElements.define('hone-product-360',HoneProduct360);
})();